import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CityCombobox } from "@/components/CityCombobox";
import { Search, ArrowLeftRight, CalendarDays } from "lucide-react";
import { wibToday, wibShift, isoToDate, dateToIso, formatDMY } from "@/lib/date";

const dateStr = (offset) => wibShift(offset);

export const SearchBar = ({ initialOrigin = "", initialDestination = "", initialService = "door_to_door" } = {}) => {
  const [cities, setCities] = useState([]);
  const navigate = useNavigate();
  const today = wibToday();
  const [form, setForm] = useState({ origin: initialOrigin, destination: initialDestination, date: today, service_type: initialService });
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const go = () => {
    const p = new URLSearchParams();
    if (form.origin) p.set("origin", form.origin);
    if (form.destination) p.set("destination", form.destination);
    if (form.date) p.set("date", form.date);
    p.set("service_type", form.service_type);
    navigate(`/cari?${p.toString()}`);
  };

  const swap = () => setForm({ ...form, origin: form.destination, destination: form.origin });

  return (
    <div className="border border-border bg-card/80 p-4 backdrop-blur-xl sm:p-6" data-testid="search-bar">
      {/* Service type tabs */}
      <div className="mb-4 flex gap-2">
        {[
          { k: "door_to_door", l: "Travel Door to Door" },
          { k: "charter", l: "Charter Drop Off" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`service-tab-${t.k}`}
            onClick={() => setForm({ ...form, service_type: t.k })}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              form.service_type === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Inputs */}
      <div className="grid gap-3 lg:grid-cols-[1fr_auto_1fr]">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">Kota Asal</label>
          <CityCombobox cities={cities} value={form.origin} onChange={(v) => setForm({ ...form, origin: v })}
            placeholder="Cari kota asal" testId="search-origin" />
        </div>

        <div className="flex items-end justify-center pb-0.5">
          <Button type="button" variant="outline" size="icon" onClick={swap} data-testid="search-swap"
            className="h-11 w-11 shrink-0 rounded-full border-primary/40 text-primary hover:bg-primary/10"
            title="Tukar asal & tujuan" aria-label="Tukar asal dan tujuan">
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        </div>

        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">Kota Tujuan</label>
          <CityCombobox cities={cities} value={form.destination} onChange={(v) => setForm({ ...form, destination: v })}
            placeholder="Cari kota tujuan" testId="search-destination" />
        </div>
      </div>

      {/* Date + submit */}
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">Tanggal Berangkat</label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" data-testid="search-date"
                className="h-11 w-full justify-start px-3 font-normal">
                <CalendarDays className="mr-2 h-4 w-4 text-primary" />
                {form.date ? formatDMY(form.date) : <span className="text-muted-foreground">tanggal/bulan/tahun</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" initialFocus
                selected={isoToDate(form.date)}
                onSelect={(d) => { if (d) { setForm({ ...form, date: dateToIso(d) }); setDateOpen(false); } }}
                disabled={(d) => dateToIso(d) < today} />
            </PopoverContent>
          </Popover>
        </div>
        <Button data-testid="search-submit" onClick={go}
          className="h-11 w-full bg-primary px-8 text-base font-bold text-primary-foreground hover:bg-primary/90 sm:w-auto">
          <Search className="mr-2 h-4 w-4" /> Cari Jadwal
        </Button>
      </div>

      {/* Quick date chips */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Cepat:</span>
        {[{ l: "Hari ini", o: 0 }, { l: "Besok", o: 1 }, { l: "Lusa", o: 2 }].map((d) => (
          <button key={d.o} type="button" data-testid={`search-date-${d.o}`} onClick={() => setForm({ ...form, date: dateStr(d.o) })}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${form.date === dateStr(d.o) ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
            {d.l}
          </button>
        ))}
      </div>
    </div>
  );
};
